import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function AIAgentsPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const aiAgentCategories = [
    {
      id: 'finance',
      title: 'Finance & Accounting Optimization',
      description: 'Improve liquidity, strengthen controls, and uncover financial inefficiencies.',
      icon: 'ri-line-chart-line',
      color: 'from-emerald-500 to-teal-600',
      services: [
        'Accounts Receivable Acceleration',
        'Bad Debt Recovery Blueprint',
        'Duplicate Payment Recovery',
        'Vendor Overpayment Analysis',
        'Cash Conversion Cycle Optimization',
        'Budget Variance Analysis',
        'Scenario‑based Cash Flow Modeling',
        'Capital Expenditure Review',
        'Profitability By Segment Analysis',
        'Business Valuation Readiness Assessment'
      ]
    },
    {
      id: 'tax',
      title: 'Tax, Compliance & Audit Readiness',
      description: 'Reduce risk, automate compliance, and prepare for regulatory scrutiny.',
      icon: 'ri-file-shield-line',
      color: 'from-teal-500 to-emerald-600',
      services: [
        'Automated Tax Filing Workflow',
        'IRS Audit Readiness Package',
        'PPP Forgiveness Compliance Review',
        'Aggregation Rule Compliance Check',
        'Wage & Hour Law Compliance Audit',
        'Employee Classification Risk Review',
        'Data Privacy Compliance Audit',
        'AI‑driven Compliance Monitoring',
        'Document Retention Policy Builder',
        'HR Compliance Workflow Builder'
      ]
    },
    {
      id: 'payroll',
      title: 'Payroll, HRIS & Workforce Automation',
      description: 'Streamline payroll, reduce errors, and strengthen workforce compliance.',
      icon: 'ri-team-line',
      color: 'from-emerald-500 to-teal-600',
      services: [
        'Payroll Process Automation Blueprint',
        'Payroll System Integration Blueprint',
        'Timekeeping System Audit',
        'Digital Onboarding Process Design',
        'Employee Turnover Analysis',
        'Workforce Diversity Analytics',
        'Overtime Cost Reduction Analysis'
      ]
    },
    {
      id: 'operations',
      title: 'Operational Efficiency & Process Automation',
      description: 'Eliminate manual work, modernize workflows, and improve cross‑department efficiency.',
      icon: 'ri-settings-3-line',
      color: 'from-teal-500 to-emerald-600',
      services: [
        'Digital Expense Reporting Implementation',
        'E‑invoicing Solution Design',
        'BI Dashboard Development',
        'Business Continuity Plan Development',
        'Supply Chain Resilience Audit',
        'Inventory Optimization Blueprint'
      ]
    },
    {
      id: 'realestate',
      title: 'Real Estate & Facilities Optimization',
      description: 'Reduce occupancy costs and improve portfolio performance.',
      icon: 'ri-building-line',
      color: 'from-emerald-500 to-teal-600',
      services: [
        'Real Estate Lease Optimization',
        'Commercial Lease Expense Review'
      ]
    },
    {
      id: 'telecom',
      title: 'Telecom, Utilities & Vendor Spend Recovery',
      description: 'Identify overcharges, recover costs, and strengthen vendor oversight.',
      icon: 'ri-phone-line',
      color: 'from-teal-500 to-emerald-600',
      services: [
        'Telecom & Utility Bill Audit',
        'Insurance Premium Audit',
        'Freight & Shipping Cost Optimization'
      ]
    },
    {
      id: 'revenue',
      title: 'Revenue Assurance & Leakage Prevention',
      description: 'Protect top‑line revenue with forensic analysis and compliance controls.',
      icon: 'ri-money-dollar-circle-line',
      color: 'from-emerald-500 to-teal-600',
      services: [
        'Channel Partner Revenue Audit',
        'Revenue Leakage Detection',
        'Franchise Fee Recovery'
      ]
    },
    {
      id: 'nonprofit',
      title: 'Nonprofit & Grant Management',
      description: 'Strengthen compliance, reporting, and grant stewardship.',
      icon: 'ri-hand-heart-line',
      color: 'from-teal-500 to-emerald-600',
      services: [
        'Nonprofit Grant Compliance Toolkit'
      ]
    },
    {
      id: 'leadership',
      title: 'Leadership, Strategy & Organizational Planning',
      description: 'Build resilient leadership pipelines and strategic readiness.',
      icon: 'ri-lightbulb-line',
      color: 'from-emerald-500 to-teal-600',
      services: [
        'Succession Planning Toolkit',
        'Training ROI Calculator',
        'Business Continuity Plan Development'
      ]
    },
    {
      id: 'risk',
      title: 'Risk Management & Regulatory Controls',
      description: 'Mitigate exposure and build defensible compliance frameworks.',
      icon: 'ri-shield-check-line',
      color: 'from-teal-500 to-emerald-600',
      services: [
        'Data Privacy Compliance Audit',
        'Employee Classification Risk Review',
        'Wage & Hour Law Compliance Audit',
        'AI‑driven Compliance Monitoring',
        'Document Retention Policy Builder'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/')} className="flex items-center hover:opacity-90 transition-opacity cursor-pointer">
              <img 
                alt="InstaVisible Tax & Savings Solutions Logo" 
                className="h-14 w-auto object-contain" 
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
              />
            </button>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-1">
              <button onClick={() => navigate('/')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Home</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('services'), 100); }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Services</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('ai-agents'), 100); }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">AI Agents</button>
              <button onClick={() => navigate('/ai-agents')} className="px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 transition-colors rounded-lg whitespace-nowrap">All AI Services</button>
              <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('contact'), 100); }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Contact</button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-2">
                <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Home</button>
                <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('services'), 100); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Services</button>
                <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('ai-agents'), 100); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">AI Agents</button>
                <button onClick={() => { navigate('/ai-agents'); setMobileMenuOpen(false); }} className="px-4 py-3 text-sm font-medium text-emerald-700 bg-emerald-50 transition-colors rounded-lg text-left whitespace-nowrap">All AI Services</button>
                <button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('contact'), 100); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Contact</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-emerald-100 border border-emerald-200 mb-8">
            <i className="ri-robot-line text-emerald-600"></i>
            <span className="text-sm text-emerald-900 font-medium">45+ Specialized AI Experts</span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
            Complete <strong>AI Tax Assistant</strong><br />
            <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 bg-clip-text text-transparent">Service Catalog</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
            Trained on specific industries and business aspects, our <strong>AI tax experts</strong> are ready to answer your specialized questions 24/7, helping you find additional savings and optimize your business with <strong>tax automation software</strong> across 10 service categories
          </p>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">10</div>
              <div className="text-sm text-gray-600">Categories</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">45+</div>
              <div className="text-sm text-gray-600">AI Tax Services</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">24/7</div>
              <div className="text-sm text-gray-600">AI Tax Support</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">100%</div>
              <div className="text-sm text-gray-600">Automated</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">∞</div>
              <div className="text-sm text-gray-600">Scalable</div>
            </div>
          </div>
        </div>
      </section>

      {/* All AI Services */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-12">
            {aiAgentCategories.map((category, index) => (
              <div key={category.id} className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-3xl overflow-hidden hover:border-emerald-400 transition-all duration-300 hover:shadow-xl">
                <div className="p-8 md:p-12">
                  <div className="flex flex-col md:flex-row gap-8">
                    {/* Category Header */}
                    <div className="md:w-1/3">
                      <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-2xl flex items-center justify-center mb-6`}>
                        <i className={`${category.icon} text-3xl text-white`}></i>
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">{category.title}</h2>
                      <p className="text-gray-600 leading-relaxed">{category.description}</p>
                      <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                        <i className="ri-check-line"></i>
                        {category.services.length} Services Available
                      </div>
                    </div>

                    {/* Services Grid */}
                    <div className="md:w-2/3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {category.services.map((service, idx) => (
                          <div key={idx} className="group p-5 bg-white border border-gray-200 rounded-xl hover:border-emerald-400 hover:shadow-md transition-all duration-300">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                                <i className="ri-check-line text-emerald-600 text-sm"></i>
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 text-sm leading-snug">{service}</h4>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 tracking-tight">
            Ready to Unlock Hidden Savings with <strong>AI Tax Technology</strong>?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Let our 45+ <strong>AI tax assistants</strong> analyze your business and uncover optimization opportunities with <strong>intelligent tax automation</strong>
          </p>
          <button onClick={() => navigate('/')} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-white text-emerald-700 hover:bg-gray-50 text-base font-semibold px-8 h-14 rounded-xl shadow-lg transition-all duration-300 hover:scale-105">
            Get Started Today
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <img 
                  alt="InstaVisible Tax & Savings Solutions Logo" 
                  className="h-14 w-auto object-contain brightness-0 invert" 
                  src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
                />
              </div>
              <p className="text-white/90 leading-relaxed max-w-md">
                <strong>Professional AI tax software</strong> for CPAs, tax professionals and CFOs. Streamline your workflow with cutting-edge <strong>tax automation technology</strong> and <strong>AI-powered tax intelligence</strong>.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Product</h3>
              <ul className="space-y-4">
                <li><button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('services'), 100); }} className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Services</button></li>
                <li><button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('ai-agents'), 100); }} className="text-white/80 hover:text-white transition-colors whitespace-nowrap">AI Agents</button></li>
                <li><button onClick={() => navigate('/ai-agents')} className="text-white/80 hover:text-white transition-colors whitespace-nowrap">All AI Services</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Company</h3>
              <ul className="space-y-4">
                <li><button onClick={() => { navigate('/'); setTimeout(() => scrollToSection('contact'), 100); }} className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Contact</button></li>
                <li><button onClick={() => navigate('/partnerships')} className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Partnerships</button></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white/80">© 2025 InstaVisible Tax & Savings Solutions. All rights reserved.</p>
            <div className="flex items-center gap-6 sm:gap-8 text-white/80 flex-wrap justify-center">
              <button className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</button>
              <button className="hover:text-white transition-colors whitespace-nowrap">Terms of Service</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
